/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Paper, useMediaQuery } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { CheckCircle } from "@mui/icons-material"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useHistory } from "react-router-dom"
import { paths, VerticaisManager } from "src/components/vendas/VerticaisManager"
import api, { apiNoLoading, IDataResponse, STATUS_CODE } from "src/framework/providers/api"
import { resetarProjeto } from "src/components/projetos/utils/resetarProjeto"
import { useObservable } from "src/framework/store/storeUtil"
import { ProjetosProvider } from "src/providers/ProjetosProvider"
import { formState$, modalComponentesDetalhesData$, personalizadoStore$ } from "src/store/vendas/projetos/store"
import { PersonalizadoStoreType } from "src/store/vendas/projetos/types"
import theme from "src/theme/light"
import { DEFAULT_PROJETO, NOVO_PROJETO_ENGENHARIA, POTENCIA_MINIMA_ENGENHARIA, POTENCIA_MINIMA_VERTICAL } from "../constants"
import { ComponenteCalcular, ListagemItem, Projeto, ProjetoCalcular, ProjetoServico, ResultadoCalculos } from "../types"
import { Acessorios } from "./containers/Acessorios"
import { Estruturas } from "./containers/Estruturas"
import { Finalizacao } from "./containers/Finalizacao"
import { Gerador } from "./containers/Gerador"
import { formatarComponentesCalcular } from "../utils/formatarComponentesCalcular"
import { salvarProjeto } from "../utils/salvarProjeto"
import { setVendedorInternoModalStatus } from "src/store/vendas/projetos/actions"
import { projetoSalvoComSucessoDialog } from "../utils/projetoSalvoComSucessoDialog"
import { redirecionarParaProjetosEspeciais } from "../utils/redirecionarParaProjetosEspeciais"
import { authUser$ } from "src/framework/components/navigations/MenuUser"
import { ExtraInfoUsuario } from "src/providers/cadastros/ContaProvider/types"
import { PermissionUtil } from "src/framework/commons/PermissionUtil"
import { ModalDetalhes as ModalDetalhesComponentes }  from '../../componentes/ModalDetalhes'
import { LoginStore } from "src/framework/store/LoginStore";
import { isLoading$ } from "src/framework/store/ApiEvents";
import { TabControl } from "src/framework/components/navigations/tabs/TabControl";
import { TabContent } from "src/framework/components/navigations/tabs/TabContent";

export type TabName = 'gerador' | 'estruturas' | 'acessorios' | 'finalizacao'

interface TabProps {
  name: TabName;
  label: string;
  done: boolean;
  disabled: boolean;
}

export interface CalcularType {
  valorServico?: number;
  tipoServico?: ProjetoServico;
}

const useStyles = makeStyles({
  tabIcon: {
    marginRight: 8,
    fontSize: 22
  }
})

const projetosProvider = new ProjetosProvider();
const manager = new VerticaisManager(paths, api, apiNoLoading);

const ProjetoPersonalizado = () => {
  const classes = useStyles()
  const history = useHistory();

  const modalComponentesDetalhesData = useObservable(modalComponentesDetalhesData$)
  const globalFormState = useObservable(formState$)
  const personalizadoStore = useObservable<PersonalizadoStoreType>(personalizadoStore$)
  const authUser = useObservable<ExtraInfoUsuario | undefined>(authUser$)
  const permissoes = useObservable(LoginStore.permissoes) || [];

  const [projeto, setProjeto] = useState<Projeto>(DEFAULT_PROJETO)
  const [formState, setFormState] = useState<Projeto>(DEFAULT_PROJETO)
  const [tab, setTab] = useState<TabName>('gerador')

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(()=>{
    if (globalFormState) {
      setFormState(globalFormState)
      setProjeto(globalFormState)
    }
  },[globalFormState])

  const inversoresAdicionados = useMemo(()=>{
    return formState.inversoresList?.filter(item => item.quantidade > 0) || []
  }, [formState.inversoresList])

  const moduloSelecionado = useMemo(()=>{
    return formState.modulosList?.find(item => item.selecionado) || null
  },[formState.modulosList])

  const acessoriosAdicionados = useMemo(()=>{
    return formState.acessorios?.filter(item => item.quantidade > 0) || []
  },[formState.acessorios])

  const estruturasAdicionadas = useMemo(()=>{
    const estruturas: ListagemItem[] = []
    const selected = formState.estruturasSelecionadas?.filter(item => {
      const total = item.items.reduce((acc, curr) => acc + curr.quantidade, 0)
      if (total > 0) return true;
      return false;
    }) || []
    selected.forEach(grupo => {
      grupo.items.forEach(item => {
        if (item.quantidade > 0) {
          estruturas.push(item)
        }
      })
    })
    return estruturas;
  },[formState.estruturasSelecionadas])

  const calcular = useCallback(async({ valorServico }: CalcularType = {})=>{
    const componentes: ComponenteCalcular[] = []

    if (moduloSelecionado) {
      componentes.push(...formatarComponentesCalcular([moduloSelecionado]))
    }

    if (inversoresAdicionados.length) {
      componentes.push(...formatarComponentesCalcular(inversoresAdicionados))
    }

    if (estruturasAdicionadas.length) {
      componentes.push(...formatarComponentesCalcular(estruturasAdicionadas))
    }

    if (acessoriosAdicionados.length) {
      componentes.push(...formatarComponentesCalcular(acessoriosAdicionados))
    }

    const getValorServico = () => {
      if (!formState.recebe_servico) return 0;
      if (formState.tipo_proposta?.value === 'gerador-sem-instalacao') return 0;
      return Number(valorServico ?? formState.valor_servico ?? 0)
    } 

    const recalcular = projetosProvider.confereRecalcular(componentes, formState);

    const regioesIds = formState.regioes.map(item => item.id)

    const payload: ProjetoCalcular = {
      componentes,
      estado_id: formState?.estado?.id || '',
      servicos: {
        tipo: getValorServico() ? 'revenda' : null,
        valor: getValorServico(),
      },
      regioes: regioesIds,
      segmento: formState.segmento,
      projeto_id: formState.clonar_projeto ? undefined : (formState.id || undefined),
      distribuidor_id: formState.distribuidor?.id || undefined,
      revenda_id: formState.revenda?.id || undefined,
      recalcular: recalcular,
    }

    const responseCalcular: IDataResponse<ResultadoCalculos> = await projetosProvider.calcular(payload)
    setFormState(old => ({ ...old, valor_servico_pis_cofins: responseCalcular.data?.servicos?.valor_com_pis_cofins || 0, resultado_calculos: responseCalcular.data }))
  },[formState.revenda?.id, formState.distribuidor?.id, formState?.id, formState?.estado?.id, formState.tipo_proposta?.value, formState.recebe_servico, acessoriosAdicionados, formState.valor_servico, formState.regioes, moduloSelecionado, formState.tipo, estruturasAdicionadas, inversoresAdicionados])
  
  const handleNextTab = useCallback(async(tabName: TabName)=>{
    setTab(tabName)
    if (tabName === 'finalizacao') {
      calcular()
    }
  },[calcular])

  const afterSave = useCallback((response: IDataResponse) => {
    isLoading$.next(false);
    projetoSalvoComSucessoDialog(response?.data?.[0]?.codigo_venda, history, {
      tipoProjeto: formState?.tipo,
    })
  }, [history, formState?.tipo])

  const save = useCallback((updatedFormState: Partial<Projeto> = {})=>{
    const payload = {
      ...formState,
      ...updatedFormState,
    }

    salvarProjeto(manager, formState.id, payload, authUser, formState?.clonar_projeto).then((response: IDataResponse) => {
      const allowedStatusCode = [STATUS_CODE.OK, STATUS_CODE.CREATED]
      if(allowedStatusCode.includes(response.statusCode)) {
        afterSave(response)
      }
    })
  },[afterSave, formState])

  const beforeSave = useCallback(() => {
    formState.lista_termo_simulacao_financiamento = formState$.value.lista_termo_simulacao_financiamento;
    const withPermission = PermissionUtil.can(PermissionUtil.PERMISSION_VENDEDORES_LISTAGEM, permissoes);
    if (withPermission && (!formState.isEdit || formState?.alterou_segmento || formState.clonar_projeto)) {
      setVendedorInternoModalStatus(true, (vendedor) => {
        save({ vendedor_interno: vendedor })
      })
    } else {
      save()
    }
  },[formState, save, permissoes])

  const potenciaTotal = useMemo<number>(() => {
    const moduloSelecionado = formState.modulosList?.find(item => item.selecionado);
    const potencia = moduloSelecionado?.potencia || 0;
    const quantidade = moduloSelecionado?.quantidade || 1;
    return Number((potencia / 1000.0 * quantidade).toFixed(2));
  },[formState?.modulosList]);

  const potenciaInvalida = useMemo<boolean>(() => {
    return potenciaTotal > POTENCIA_MINIMA_ENGENHARIA;
  },[potenciaTotal]);

  const alertaProjetoPotencia = useCallback(async() => {
    if (potenciaInvalida) {
      redirecionarParaProjetosEspeciais({
        onNegation: () => {
          formState$.next({...resetarProjeto(formState), ...NOVO_PROJETO_ENGENHARIA })
          history.push('/projetos/verticais/novo',{ allowAccess: true })
        },
      });
    }
  },[potenciaInvalida])

  const handleSave = useCallback(async() => {
    if (potenciaInvalida) {
      alertaProjetoPotencia();
      return;
    }

    beforeSave();
  },[beforeSave, formState, potenciaInvalida])

  const alterarCodigoVenda = useCallback((codigoVenda, segmento) => {
    const vertical = segmento?.includes('vertical');
    if (vertical && !codigoVenda?.includes('PROV')) {
      return codigoVenda?.replace('PROJ', 'PROV');
    } else if (!vertical && !codigoVenda?.includes('PROJ')) {
      return codigoVenda?.replace('PROV', 'PROJ');
    }
    return codigoVenda;
  }, []);


  useEffect(() => {
    const recalcular = async () => {
      let segmentoProjeto = projeto.segmento;
      if (potenciaTotal > POTENCIA_MINIMA_VERTICAL) {
        if (projeto.segmento?.includes('offgrid')) segmentoProjeto = 'vertical_offgrid';
        else segmentoProjeto = 'vertical_ongrid';
      } else {
        if (projeto.segmento?.includes('offgrid')) segmentoProjeto = 'distribuicao_offgrid';
        else segmentoProjeto = 'distribuicao_ongrid';
      }
      
      const alterouSegmento = formState.segmento !== segmentoProjeto;
      if (alterouSegmento) {
        const state = {...formState};
        const codigoVenda = alterarCodigoVenda(formState.codigo_venda, segmentoProjeto);

        state.segmento = segmentoProjeto;
        state.codigo_venda = codigoVenda;
        state.alterou_segmento = alterouSegmento;
        state.auto_comissao = true;
        state["@proposta_atualizada"] = true;
        setFormState(state);
      }
    }

    const alterouCodigoVenda = formState.codigo_venda && formState.codigo_venda !== projeto.codigo_venda;
    if ((potenciaTotal && Number(potenciaTotal) !== Number(projeto?.potencia)) || alterouCodigoVenda) {
      recalcular();
    }
  }, [potenciaTotal, projeto, formState.codigo_venda])


  const tabs = useMemo<TabProps[]>(() => {
    const estruturasDisabled = personalizadoStore?.tabs.estruturas.disabled || false
    const acessoriosDisabled = estruturasDisabled || personalizadoStore?.tabs.acessorios.disabled || false
    const finalizacaoDisabled = acessoriosDisabled || personalizadoStore?.tabs.finalizacao.disabled || false
    
    const geradorDone = !estruturasDisabled
    const estruturasDone = !acessoriosDisabled
    const acessoriosDone = !finalizacaoDisabled
    const finalizacaoDone = personalizadoStore?.tabs.finalizacao.done || false

    return [
      {
        name: 'gerador',
        label: 'Gerador',
        done: geradorDone || false,
        disabled: false
      },
      { 
        name: 'estruturas',
        label: 'Estruturas',
        done: estruturasDone || false,
        disabled: estruturasDisabled
      },
      {
        name: 'acessorios',
        label: 'Acessórios',
        done: acessoriosDone,
        disabled: acessoriosDisabled
      },
      {
        name: 'finalizacao',
        label: 'Finalização',
        done: finalizacaoDone,
        disabled: finalizacaoDisabled
      },
    ]
  },[personalizadoStore])

  return (
    <>
      <Paper elevation={0} className="tab-header">
        <TabControl
          indicatorColor="primary" 
          onChangeTab={() => {}}
          currentTabName={tab}
        >
          {tabs.map(item => (
            <TabContent
              disabled
              name={item.name}
              visible={true}
              label={
                <Box display={isMobile ? "none" : "flex"} justifyContent={"center"} alignItems="center">
                  <CheckCircle htmlColor={item.done ? theme.palette.primary.dark : 'unset'} className={classes.tabIcon} />
                  <span style={{ color: theme.palette.text.secondary }}>{item.label}</span>     
                </Box>
              }>

              {item.name === 'gerador' && tab === 'gerador' && (
                <Gerador
                  formState={formState}
                  onClickBack={() => {
                    formState$.next({ ...formState$.value, ...formState })

                    if (formState.clonar_projeto) {
                      history.push(`/projetos/${formState.id}/clonar`, { noReset: true })
                      return
                    }

                    if (formState.isEdit) {
                      history.push(`/projetos/${formState.id}/edicao`, { noReset: true })
                      return
                    }

                    history.push('/projetos', { noReset: true })
                  }} 
                  onChangeFormState={setFormState}
                  onClickNext={() => handleNextTab('estruturas')}
                />
              )}

              {item.name === 'estruturas' && tab === 'estruturas' && (
                <Estruturas
                  tab={tab}
                  formState={formState} 
                  onChangeFormState={(state: Projeto) => setFormState(state)} 
                  onClickBack={() => handleNextTab('gerador')} 
                  onClickNext={() => handleNextTab('acessorios')} 
                />
              )}

              {item.name === 'acessorios' && tab === 'acessorios' && (
                <Acessorios
                  formState={formState} 
                  onChangeFormState={(state: Projeto) => setFormState(state)} 
                  onClickBack={() => handleNextTab('estruturas')} 
                  onClickNext={() => handleNextTab('finalizacao')} 
                />
              )}

              {item.name === 'finalizacao' && tab === 'finalizacao' && (
                <Finalizacao
                  calcular={calcular}
                  formState={formState}
                  onChangeFormState={setFormState}
                  onClickBack={() => handleNextTab('acessorios')}
                  onClickNext={handleSave}
                />
              )}
            </TabContent>
          ))}
        </TabControl>
      </Paper>

      {!!modalComponentesDetalhesData && (
        <ModalDetalhesComponentes 
          open={!!modalComponentesDetalhesData}
          componentId={modalComponentesDetalhesData}
          setOpenModal={(openStatus: any) => {
            if (!openStatus) modalComponentesDetalhesData$.next(0)
          }}
        />
      )}
    </>
  )
}

export { ProjetoPersonalizado }